import {
  faInfoCircle,
  faShield,
  faTable,
  faTableLayout,
  faUser,
} from "@fortawesome/pro-regular-svg-icons";
import { Root } from "@radix-ui/react-accordion";
import { Controller, useFormContext } from "react-hook-form";

import { useWorkspaceUsers } from "src/api/taktile/queries";
import { Card } from "src/base-components/Card";
import {
  EditorAccordionItem as AccordionItem,
  accordionRootClassName,
} from "src/base-components/EditorAccordionItem";
import { ErrorHint } from "src/base-components/ErrorHint";
import { Icon } from "src/base-components/Icon";
import { Input } from "src/base-components/Input";
import { Label } from "src/base-components/Label";
import { Select } from "src/base-components/Select";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { DEFAULT_STRING_LENGTH } from "src/base-components/schemaKit/validators";
import { IconPicker } from "src/cases/common/IconPicker";
import { useCaseTemplatesWithVersions } from "src/cases/queries/templates";
import { CaseQueueFormValues } from "src/cases/queues/types";
import { getCaseQueueIcon, CASE_QUEUE_ICONS } from "src/cases/queues/utils";
import { Avatar } from "src/design-system/Avatar";
import { Callout } from "src/design-system/Callout";
import { EmptyState } from "src/design-system/EmptyState";
import { CaseConfigurationPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";
import { canUserBeAssignee, getUserName } from "src/utils/user";

const CaseQueueMembersField: React.FC<{
  isLoading?: boolean;
}> = ({ isLoading: isLoadingQueue }) => {
  const { formState } = useFormContext<CaseQueueFormValues>();
  const { orgId, wsId } = useParamsDecode<CaseConfigurationPageParams>();
  const { data: users = [], isLoading: isLoadingUsers } = useWorkspaceUsers(
    orgId,
    wsId,
    {
      include_deactivated: false,
    },
  );

  const assignees = users.filter((user) => canUserBeAssignee(wsId, user));

  const membersOptions = assignees.map((user) => ({
    key: user.id,
    searchText: getUserName(user),
    value: (
      <span className="flex items-center gap-x-2">
        <Avatar user={user} />
        {getUserName(user)}
      </span>
    ),
  }));

  return (
    <div className="flex flex-col">
      <Label>Select Members</Label>
      <Controller
        defaultValue={[]}
        name="memberIds"
        render={({ field }) => {
          return (
            <Select
              dataLoc="case-queue-members-select"
              disabled={isLoadingQueue || isLoadingUsers}
              dropdownPlaceholder={
                <EmptyState
                  description="No users matching your search. Try adjusting your search term."
                  headline="No users found"
                  icon={faUser}
                />
              }
              loading={isLoadingQueue || isLoadingUsers}
              options={membersOptions}
              placeholder="Members"
              value={field.value}
              multiple
              searchable
              showResetButton
              onChange={field.onChange}
            />
          );
        }}
      />
      {formState.errors.memberIds && (
        <ErrorHint>{formState.errors.memberIds.message}</ErrorHint>
      )}
    </div>
  );
};

const CaseQueueCaseTemplatesField: React.FC<{
  isLoading?: boolean;
}> = ({ isLoading: isLoadingQueue }) => {
  const { formState } = useFormContext<CaseQueueFormValues>();
  const { data: templates, isLoading: isLoadingTemplates } =
    useCaseTemplatesWithVersions();

  const caseTemplatesOptions =
    templates?.map((template) => ({
      key: template.id,
      searchText: template.attributes.template_name,
      value: (
        <span className="flex items-center gap-x-2">
          <Icon
            color="text-gray-600"
            icon={getCaseQueueIcon(template.attributes.icon) ?? faTable}
            size="xs"
          />
          {template.attributes.template_name}
        </span>
      ),
    })) ?? [];

  return (
    <div className="flex flex-col">
      <Label>Select templates</Label>
      <Controller
        defaultValue={[]}
        name="templateIds"
        render={({ field }) => {
          return (
            <Select
              dataLoc="case-queue-case-templates-select"
              disabled={isLoadingQueue || isLoadingTemplates}
              dropdownPlaceholder={
                <EmptyState
                  description="No case templates matching your search. Try adjusting your search term."
                  headline="No case templates found"
                  icon={faTableLayout}
                />
              }
              loading={isLoadingQueue || isLoadingTemplates}
              options={caseTemplatesOptions}
              placeholder="Case templates"
              placement="top"
              value={field.value}
              multiple
              searchable
              showResetButton
              onChange={field.onChange}
            />
          );
        }}
      />
      {formState.errors.templateIds && (
        <ErrorHint>{formState.errors.templateIds.message}</ErrorHint>
      )}
    </div>
  );
};

export const CaseQueueConfiguration: React.FC<{
  isLoading?: boolean;
}> = ({ isLoading }) => {
  const form = useFormContext<CaseQueueFormValues>();
  const { queue_id } = useParamsDecode<CaseConfigurationPageParams>();
  const isEditing = !!queue_id;

  const isLoadingQueue = isEditing && isLoading;

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="flex flex-col gap-y-2 border-b border-gray-200 p-5">
        <h2 className="font-inter-semibold-13px">Define queue settings</h2>
        <p className="text-gray-500 font-inter-normal-12px">
          Provide basic details for the queue
        </p>
      </div>

      <div className="space-y-3 p-5">
        <Root
          className={accordionRootClassName}
          defaultValue={[
            "queue-name",
            "set-membership",
            "select-case-template",
          ]}
          type="multiple"
        >
          <AccordionItem
            className="p-4 pt-0"
            title="Define the queue name"
            value="queue-name"
            headerAsChild
          >
            <p className="text-gray-500 font-inter-normal-12px">
              Define a name for this case queue.
            </p>
            <Card className="my-3 w-full">
              {isLoadingQueue ? (
                <div className="flex gap-x-2">
                  <SkeletonPlaceholder height="h-15" width="w-15" />
                  <div className="flex flex-1 flex-col gap-y-1.5">
                    <SkeletonPlaceholder height="h-4" width="w-full" />
                    <SkeletonPlaceholder height="h-4" width="w-110" />
                    <SkeletonPlaceholder height="h-4" width="w-90" />
                  </div>
                </div>
              ) : (
                <div className="flex gap-x-2">
                  <Controller
                    control={form.control}
                    defaultValue="shield-halved"
                    name="attributes.icon"
                    render={({ field }) => (
                      <IconPicker
                        dataLoc="case-queue-icon-picker"
                        options={CASE_QUEUE_ICONS}
                        placeholder={faShield}
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                  <div className="flex flex-1 flex-col">
                    <Label required>Name</Label>
                    <Input
                      data-loc="case-queue-name-input"
                      errored={!!form.formState.errors.attributes?.name}
                      placeholder="Queue name"
                      fullWidth
                      {...form.register("attributes.name", {
                        required: "Queue name is required",
                        validate: (value) =>
                          value.trim().length > 0 ||
                          "Queue name cannot be empty",
                        maxLength: {
                          value: DEFAULT_STRING_LENGTH,
                          message: `Queue name must not be more than ${DEFAULT_STRING_LENGTH} characters`,
                        },
                      })}
                    />
                    {form.formState.errors.attributes?.name && (
                      <ErrorHint dataLoc="case-queue-name-error-message">
                        {form.formState.errors.attributes.name.message}
                      </ErrorHint>
                    )}
                  </div>
                </div>
              )}
            </Card>
          </AccordionItem>
          <AccordionItem
            className="p-4 pt-0"
            title="Set membership"
            value="set-membership"
          >
            <p className="text-gray-500 font-inter-normal-12px">
              Select members to work on this queue. Only the members selected
              here can review cases in this queue.
            </p>
            <Card className="my-3 flex w-full flex-col gap-y-2">
              <CaseQueueMembersField isLoading={isLoadingQueue} />
              {isEditing && form.formState.dirtyFields.memberIds && (
                <Callout
                  dataLoc="case-queue-members-callout-warning"
                  type="warning"
                >
                  Cases assigned to removed members will not be automatically
                  unassigned and will remain linked to them. You must manually
                  unassign these cases if needed.
                </Callout>
              )}
            </Card>
            <p className="flex gap-x-1 text-gray-500 font-inter-normal-12px">
              <Icon color="text-gray-600" icon={faInfoCircle} size="xs" /> All
              eligible queue members can view and manually assign unassigned
              cases to themselves. Admins can reassign the cases to other queue
              members.
            </p>
          </AccordionItem>
          <AccordionItem
            className="p-4 pt-0"
            title="Select case template"
            value="select-case-template"
          >
            <p className="text-gray-500 font-inter-normal-12px">
              Select the case templates to include in this queue
            </p>
            <Card className="my-3 flex w-full flex-col gap-y-2">
              <CaseQueueCaseTemplatesField isLoading={isLoadingQueue} />
              {isEditing && form.formState.dirtyFields.templateIds && (
                <Callout
                  dataLoc="case-queue-case-templates-callout-warning"
                  type="warning"
                >
                  Cases with deleted templates will be marked as deleted and
                  will need to be manually archived if necessary. You can
                  continue to operate on them as usual.
                </Callout>
              )}
            </Card>
          </AccordionItem>
        </Root>
      </div>
    </div>
  );
};
